import React, { useEffect, useState, useMemo, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import "../../index.css";
import "../../App.css";
import "../../styles/toggle.css";
import "aos/dist/aos.css";
import { CgProfile } from "react-icons/cg";
import { RiCloseLine, RiMenuLine } from "react-icons/ri";

const Header = () => {
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("#home");
  const isMenuActive = useMediaQuery({ maxWidth: 768 });
  const isMobile = useMediaQuery({ maxWidth: 640 });

  const navLinks = useMemo(() => [
    { href: '#home', label: 'ABOUT' },
    { href: '#experience', label: 'EXPERIENCE' },
    { href: '#skills', label: 'SKILLS' },
    { href: '#education', label: 'EDUCATION' },
    { href: '#portfolio', label: 'PROJECTS' },
    { href: '#contact', label: 'CONTACT' },
  ], []); // empty dependency array = only created once

  const navStyle = "text-black dark:text-white hover:text-primaryColor dark:hover:text-primaryColor font-[600] transition-colors duration-300"

  const menuClassName = `menu navbar pr-20 mx-auto ${isOpen ? "show__menu" : ""}`;

  const stickyHeader = () => {
    window.addEventListener("scroll", () => {
      if (headerRef.current !== null) {
        if (
          document.body.scrollTop > 80 ||
          document.documentElement.scrollTop > 80
        ) {
          headerRef.current.classList.add("sticky__header");
        }
        else {
          headerRef.current.classList.remove("sticky__header");
        }
      }
    });
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = navLinks.map(link => document.querySelector(link.href));
      const scrollPosition = window.scrollY + 100;

      sections.forEach((section, index) => {
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;

          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(navLinks[index].href);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [navLinks]);

  useEffect(() => {
    stickyHeader();
    return () => window.removeEventListener("scroll", stickyHeader);
  }, []);

  const handleNavItemClick = (e, href) => {
    e.preventDefault();
    setIsOpen(false);

    // Prevent body scroll when menu is closing
    document.body.style.overflow = '';

    const targetAttr = href;
    const element = document.querySelector(targetAttr);
    if (element) {
      const headerHeight = isMobile ? 70 : 80;
      const location = element.offsetTop;
      window.scrollTo({
        top: location - headerHeight,
        left: 0,
        behavior: "smooth"
      });
      setActiveSection(targetAttr);
    }
  };

  const toggleMenu = () => {
    const newState = !isOpen;
    setIsOpen(newState);

    // Prevent body scroll when menu is open
    if (newState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    if (menuRef.current !== null) {
      menuRef.current.classList.toggle("show__menu");
    }
  };

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        document.body.style.overflow = '';
        if (menuRef.current !== null) {
          menuRef.current.classList.remove("show__menu");
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const [theme, setTheme] = useState(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) return savedTheme;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.add("light");
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const handleThemeSwitch = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <header
      ref={headerRef}
      className={`w-full flex items-center bg-white dark:bg-gray-700 transition-all duration-300 shadow-sm ${isMobile ? 'h-[70px] leading-[70px]' : 'h-[80px] leading-[80px]'
        }`}
    >
      <div className="container mx-auto px-3 sm:px-4 w-full">
        <div className="flex items-center justify-between w-full">
          {/* logo start */}
          <div className="flex items-center gap-1 sm:gap-2 pr-2 sm:pr-4 md:pr-10 flex-shrink-0 min-w-0">
            <div
              className="flex items-center group cursor-pointer min-w-0"
              onClick={(e) => {
                e.preventDefault();
                handleNavItemClick(e, '#home');
              }}
            >
              <CgProfile className={`fill-current text-primaryColor dark:text-blue-400 group-hover:scale-110 transition-transform duration-300 flex-shrink-0 ${isMobile ? 'h-5 w-5 mr-1.5' : 'h-7 w-7 mr-2'
                }`} />
              <p className={`font-bold text-black dark:text-white group-hover:text-primaryColor dark:group-hover:text-blue-400 transition-colors duration-300 truncate ${isMobile ? 'text-sm sm:text-base' : 'text-lg md:text-xl'
                }`}>
                {isMobile ? 'M. LEACHOURI' : 'MOHAMED LEACHOURI'}
              </p>
            </div>
          </div>
          {/* logo end */}

          {/* nav start */}
          <nav className={`hidden md:flex items-center ${menuClassName}`} ref={menuRef}>
            <ul className="flex items-center justify-center gap-6 lg:gap-8">
              {navLinks.map((item) => {
                const isActive = activeSection === item.href;
                return (
                  <li key={item.href}>
                    <a
                      onClick={(e) => handleNavItemClick(e, item.href)}
                      className={`${navStyle} relative px-2 py-1 ${isActive
                        ? 'text-primaryColor dark:text-blue-400 font-bold'
                        : ''
                        }`}
                      href={item.href}
                    >
                      {item.label}
                      {isActive && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primaryColor dark:bg-blue-400 transition-all duration-300"></span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
          {/* nav end */}

          {/* Theme toggle and mobile menu button */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
            {/* Theme Toggle */}
            <label className={`switch cursor-pointer ${isMobile ? 'scale-90' : ''}`}>
              <span className="sun">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <circle r="5" cy="12" cx="12"></circle>
                  <path d="m21 13h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 0 2zm-17 0h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 0 2zm13.66-5.66a1 1 0 0 1 -.66-.29 1 1 0 0 1 0-1.41l.71-.71a1 1 0 1 1 1.41 1.41l-.71.71a1 1 0 0 1 -.75.29zm-12.02 12.02a1 1 0 0 1 -.71-.29 1 1 0 0 1 0-1.41l.71-.66a1 1 0 0 1 1.41 1.41l-.71.71a1 1 0 0 1 -.7.24zm6.36-14.36a1 1 0 0 1 -1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1 -1 1zm0 17a1 1 0 0 1 -1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1 -1 1zm-5.66-14.66a1 1 0 0 1 -.7-.29l-.71-.71a1 1 0 0 1 1.41-1.41l.71.71a1 1 0 0 1 0 1.41 1 1 0 0 1 -.71.29zm12.02 12.02a1 1 0 0 1 -.7-.29l-.66-.71a1 1 0 0 1 1.36-1.36l.71.71a1 1 0 0 1 0 1.41 1 1 0 0 1 -.71.24z"></path>
                </svg>
              </span>
              <span className="moon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                  <path d="m223.5 32c-123.5 0-223.5 100.3-223.5 224s100 224 223.5 224c60.6 0 115.5-24.2 155.8-63.4 5-4.9 6.3-12.5 3.1-18.7s-10.1-9.7-17-8.5c-9.8 1.7-19.8 2.6-30.1 2.6-96.9 0-175.5-78.8-175.5-176 0-65.8 36-123.1 89.3-153.3 6.1-3.5 9.2-10.5 7.7-17.3s-7.3-11.9-14.3-12.5c-6.3-.5-12.6-.8-19-.8z"></path>
                </svg>
              </span>
              <input
                type="checkbox"
                className="input"
                checked={theme === "dark"}
                onChange={handleThemeSwitch}
                aria-label="Toggle dark mode"
              />
              <span className="slider"></span>
            </label>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className={`md:hidden text-primaryColor dark:text-blue-400 cursor-pointer hover:scale-110 active:scale-95 transition-transform duration-200 z-50 p-2 -mr-2 ${isMobile ? 'text-2xl' : 'text-3xl'
                }`}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <RiCloseLine /> : <RiMenuLine />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuActive && (
          <>
            {/* Mobile Menu Overlay - Show first for proper z-index */}
            <div
              className={`fixed inset-0 bg-black transition-opacity duration-300 md:hidden ${isOpen ? 'opacity-50 z-40' : 'opacity-0 z-[-1] pointer-events-none'
                }`}
              onClick={toggleMenu}
              aria-hidden="true"
            />

            {/* Mobile Menu Sidebar */}
            <div
              className={`fixed top-0 right-0 h-full w-[280px] sm:w-[320px] bg-white dark:bg-gray-700 shadow-2xl transform transition-transform duration-300 ease-out z-50 ${isOpen ? "translate-x-0" : "translate-x-full"
                }`}
              ref={menuRef}
              role="navigation"
              aria-label="Mobile navigation"
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                  Menu
                </h2>
                <button
                  onClick={toggleMenu}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:text-primaryColor dark:hover:text-primaryColorDark transition-colors"
                  aria-label="Close menu"
                >
                  <RiCloseLine className="text-2xl" />
                </button>
              </div>

              {/* Menu Items */}
              <div className="flex flex-col pt-6 px-4 pb-4 h-[calc(100%-80px)] overflow-y-auto">
                <ul className="flex flex-col gap-2 w-full">
                  {navLinks.map((item) => {
                    const isActive = activeSection === item.href;
                    return (
                      <li key={item.href} className="w-full">
                        <a
                          onClick={(e) => handleNavItemClick(e, item.href)}
                          className={`${navStyle} block w-full py-3 px-4 rounded-lg transition-all duration-200 text-base ${isActive
                            ? 'text-primaryColor dark:text-primaryColorDark bg-blue-50 dark:bg-gray-600 font-bold shadow-sm'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-600 active:bg-gray-200 dark:active:bg-gray-500'
                            }`}
                          href={item.href}
                        >
                          {item.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
